import signals from "signals"
import loadMedia from "./load_media"

// will collect promises from the first lazy loads on page load
const initialLazyLoadingPromises = []

const lazyLoader = {
  init: async() => {
    lazyLoader.add(document.querySelectorAll("[data-lazy-load]"), loadMedia)
    await new Promise(resolve => setTimeout(resolve, 500))
    return Promise.all(initialLazyLoadingPromises)
  },
  add: (mediaElements, callback, options = {}) => {
    if (!mediaElements) return
    if (mediaElements.length === 0) return

    if (!mediaElements.length) {
      mediaElements = [mediaElements]
    }

    const defaultOptions = {
      // the intersector should notify 300px before it reaches the viewport
      bottomMargin: "300px",
      // Set it to false if you want to get notified when the target leaves
      // the viewport.
      isIntersecting: true,
      // By default we remove a target from the observer list once it intersects
      // with the viewport. Set it to true if you want to keep observing it.
      continuously: false
    }
    options = {...defaultOptions, ...options}
    const intersectionObserver =
      createIntersectionObserver(callback, options)
    for (const element of mediaElements) {
      if (intersectionObserver) {
        intersectionObserver.observe(element)
      // don't call it immediately if checking for the not intersecting case
      } else if (options.isIntersecting) {
        call(callback, element)
      }
    }
    if (!intersectionObserver) signals.emit("kenhub.page_layout_changed")
  }
}

function createIntersectionObserver(callback, options) {
  if (!window.IntersectionObserver || window.gon.disableLazyLoading) return null

  function _intersectionHandler(entries, observer) {
    for (const entry of entries) {
      if (entry.isIntersecting !== options.isIntersecting) continue

      if (!options.continuously) observer.unobserve(entry.target)
      call(callback, entry.target)
    }
  }

  return new IntersectionObserver(
    _intersectionHandler,
    // This value extends the viewport with a certain margin.
    // The first value (margin-top) is negative because we don't want to trigger
    // an intersection with the elements which are above the viewport because it
    // would make the layout flicker and because we have a sticky middlebar
    // which decreases the visible space at the top
    {rootMargin: `-80px 0px ${options.bottomMargin} 0px`}
  )
}

// executes the callback and triggers the page layout changed event
function call(callback, element) {
  const result = callback(element)
  const promise = result?.then ? result : Promise.resolve()
  initialLazyLoadingPromises.push(promise)
  promise.then(() => signals.emit("kenhub.page_layout_changed"))
}

export default lazyLoader
